import { Observable, forkJoin } from 'rxjs';
import { AppSettingActions } from './../store/app-settings.reducer';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { IState } from '../store/root.reducer';
import { LocalStorageService } from 'angular-2-local-storage';
import { ICryptoRequest } from 'src/app/shared/domain/crypto-request.entity';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';

const accessTokenKey = 'ops.id.accessToken';

export class AppSettingEntities {
  public static CONTACT = 'contact';
  public static SUPPLIER = 'supplier';
  public static SHOP = 'shop';
}

@Injectable({
  providedIn: 'root',
})
export class AppSettingsService {
  apiUrl = environment.commonAPI + 'app-settings/';

  private appId: any = null;
  private settings: AppSetting[] = [];

  constructor(
    private httpClient: HttpClient,
    private store: Store<IState>,
    private localStorage: LocalStorageService) {
  }

  Set(body: SaveAppSettingsRequest): Observable<any> {
    return this.httpClient.post<any>(this.apiUrl + 'set/v2', body, this.getOptions());
  }

  Get(body: IAppSettingsRequest): Observable<AppSetting[]> {
    return this.httpClient.post<AppSetting[]>(this.apiUrl + 'settings/v2', body, this.getOptions());
  }

  GetApplicationNavigationAll(body: IApplicationNavigationAllRequest): Observable<IApplicationNavigationAllResponse[]> {
    return this.httpClient.post<IApplicationNavigationAllResponse[]>(this.apiUrl + 'navigation/all', body);
  }

  private getOptions() {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    return { headers };
  }

  private getSettings(appId: number, entityType: string): Observable<AppSetting[]> {
    const params = this.getAppSettingsRequest(appId, entityType);
    return this.Get(params);
  }

  private getAppSettingsRequest(appId: number, entityType: string, settingKey?: string): IAppSettingsRequest {
    return {
      entityType: entityType,
      appId: appId,
      settingKey: settingKey ? settingKey : null,
      encrypted: true
    };
  }

  writeSetting(requests: SaveAppSetting[]): void {
    this.store.pipe(select((state: IState) => state.app.applicationId)).subscribe(applicationId => {
      this.appId = applicationId;
    }).unsubscribe();
    const settingRequest: SaveAppSettingsRequest = <SaveAppSettingsRequest>{};
    settingRequest.settings = [];
    settingRequest.encrypted = true;
    requests.map(setting => {
      if (setting.appId === null || setting.appId === undefined || setting.appId.toString() === '') {
        setting.appId = this.appId;
      }
      if (typeof setting.value === 'number') {
        setting.value = setting.value.toString();
      }
      settingRequest.settings.push(setting);
    });
    this.Set(settingRequest).subscribe(res => {
        if (res.result !== true) {
          return null;
        }
        this.reloadSettings();
        return res;
    });
  }

  reloadSettings(): Promise<any> {
    this.store.dispatch({
      type: AppSettingActions.SET_IS_LOADED,
      payload: false
    });
    const promise = new Promise(resolve => {
      if (this.getAccessToken()) {
        let contactType = 0;
        this.store.pipe(select((state: IState) => state.currentUser))
        .subscribe(currentUser => {
          if (currentUser && currentUser.contactId !== 0) {
            contactType = currentUser.userType;
          }
        });
        this.store.pipe(select((state: IState) => state.app.applicationId)).subscribe(applicationId => {
          this.appId = applicationId;
        });
        if (this.appId) {
          forkJoin([
            this.getSettings(this.appId, AppSettingEntities.CONTACT),
            this.getSettings(this.appId, contactType === 1 ? AppSettingEntities.SHOP : AppSettingEntities.SUPPLIER)
          ]).subscribe(settings => {
            this.store.dispatch({
              type: AppSettingActions.SET_SETTINGS,
              payload: settings[0].concat(settings[1])
            });
            this.store.dispatch({
              type: AppSettingActions.SET_IS_LOADED,
              payload: true
            });
            resolve();
          });
        }
      }
    });
    return promise;
   }

   private getAccessToken(): string {
    const token = this.localStorage.get(accessTokenKey);
    if (token) {
      return token.toString();
    }
    return '';
  }

  getSetting(key: string): AppSetting {
    this.store.pipe(select((store: IState) => store.appSettings.Settings)).subscribe(settings => {
      this.settings = settings;
    }).unsubscribe();

    const setting = this.settings.filter(s => s.key.toUpperCase() === key.toUpperCase());
    if (setting.length > 0) {
      return setting[0] as AppSetting;
    } else {
      return <AppSetting>{};
    }
  }
}

interface SaveAppSettingsRequest extends ICryptoRequest {
  settings: SaveAppSetting[];
}

interface IAppSettingsRequest extends ICryptoRequest {
  entityType: string;
  appId: number;
  settingKey: string;
}

export interface SaveAppSetting {
  entityType: string;
  appId?: number;
  key: string;
  value: string | number;
}

export interface AppSetting {
  key: string;
  value: string | number;
  appId?: number;
  name?: string;
  description?: string;
  internal?: boolean;
  lastUpdate?: string | object;
  featureCode?: string;
  categoryName?: string;
  categoryOrder?: number;
  options?: AppSettingOption[];
  dataType: string;
  entityType: string;
}

export interface AppSettingOption {
  settingKeyId: number;
  settingKey: string;
  settingOptionName: string;
  settingOptionValue: string | object;
  defaultValue: string;
}

interface IApplicationNavigationAllRequest extends ICryptoRequest {
  appId: number;
  companyType: number;
  companyId: number;
  userId: number;
}

export interface IApplicationNavigationAllResponse {
  applicationId: number;
  name: string;
}
